import React, { ChangeEvent, useRef, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faXmark } from "@fortawesome/pro-regular-svg-icons";
import { useTranslation } from "react-i18next";

import { showError } from "redux/actions/ui";

import { useAppDispatch } from "hooks/redux";

import Sheet from "components/sheet/Sheet";
import { ButtonNew } from "components/buttonNew";
import Form from "components/form/Form";
import FormLayout from "components/form/FormLayout";
import Input from "components/form/input/Input";
import { Badge } from "components/badge/Badge";
import Portal from "elements/Portal";
import CheckboxBlock from "../elements/CheckboxBlock";
import CustomSelect from "../elements/CustomSelect";

interface ICustomSelectEdit {
  label: string;
  array: string[];
  helpText: string;
  required: boolean;
  saveInput: (label: string, array: string[], helpText: string, required: boolean) => void;
  editModalOpen: boolean;
  closeEditModal: () => void;
  /** Viewable INPUT on the edit page itself. */
  children: React.ReactNode;
  hideRequired?: boolean;
}

/** Structural display wrapper containing edit modal and its state. */
export default function CustomSelectEdit(props: ICustomSelectEdit) {
  const { label, array, helpText = "", required, editModalOpen, closeEditModal, hideRequired } = props;

  const { t } = useTranslation();
  const selectInputRef = useRef<HTMLInputElement>(null);
  const dispatch = useAppDispatch();

  const [state, setState] = useState({
    input: "",
    label: label,
    helpText: helpText,
    array: array !== null ? array : [],
    required: required,
  });

  const [editing, setEditing] = useState({
    state: false, // style trigger
    value: null, // value currently being edited
  });

  function addListItem() {
    if (state.input.length === 0 && !editing.state) {
      dispatch(showError(t("Please enter a value to add an option to the select.")));
      return;
    }

    // if already found, return early
    if (state.array?.find(val => val === state.input)) {
      if (editing.state && editing.value == state.input) {
        setEditing({ state: false, value: null }); // remove edit state
        setState(prev => ({ ...prev, input: "" })); // remove input value
        return;
      }

      dispatch(showError(t("The value is already in the dropdown list.")));
      return;
    }

    // 'switch' the value if editing
    if (editing.state) {
      updateListItem(state.input);
      return;
    }

    // push new value if not editing
    const returnArray = state?.array ? [...state.array] : [];
    returnArray.push(state.input);

    setState(prev => ({ ...prev, input: "", array: returnArray }));
  }

  // replace list item with editing value.  Remove if value.length == 0
  function updateListItem(value: string) {
    if (value.length === 0) {
      removeListItem(editing.value);
      setEditing({ state: false, value: null });
      return;
    }

    const editReturn = state.array.map(val => (val === editing.value ? value : val));

    setState(prev => ({ ...prev, input: "", array: editReturn }));
    setEditing({ state: false, value: null });
  }

  // splice value from select options
  function removeListItem(value: string) {
    const returnArray = [...state.array];

    if (returnArray.includes(value)) {
      returnArray.splice(returnArray.indexOf(value), 1);
      setState(prev => ({ ...prev, array: returnArray }));
    }
  }

  // Click action
  function selectListItem(isSelected: boolean, value: string) {
    if (editing.state) {
      return;
    } // disallow row click if editing a value
    setState(prev => ({ ...prev, input: isSelected ? "" : value }));
  }

  // Handle editing state of the CustomSelect
  function listIconClick(e: React.MouseEvent<HTMLElement, MouseEvent>, value: string, selected: boolean) {
    e.stopPropagation();

    // Default icon functionality
    if (!editing.state && !selected) {
      removeListItem(value);
      return;
    }

    // Set editing state
    if (!editing.state && selected) {
      setEditing({ state: true, value: value });
      return;
    }

    // Remove editing state
    setEditing(prev => ({
      ...prev,
      state: false,
      value: null,
    }));

    setState(prev => ({ ...prev, input: "" }));
  }

  return (
    <div className={`input-view-wrap${required ? " required" : ""}`}>
      {props.children}

      {/* SELECT input modal */}
      <Portal isMounted={editModalOpen}>
        <Sheet
          title={t("Dropdown")}
          open={editModalOpen}
          onOk={() => props.saveInput(state.label, state.array, state.helpText, state.required)}
          onCancel={() => {
            closeEditModal();
            setState({
              label: label,
              helpText: helpText,
              array: array,
              required: required,
              input: "",
            });
          }}
          size="small"
          okText={t("Save")}
        >
          <Form>
            <FormLayout>
              <FormLayout.Group>
                <Input
                  label={t("Status Name")}
                  defaultValue={label}
                  onChange={(e: ChangeEvent<HTMLInputElement>) =>
                    setState(prev => ({ ...prev, label: e.target.value }))
                  }
                  className="input-edit-action"
                />
              </FormLayout.Group>
              <FormLayout.Group>
                <Input
                  label={t("Status Help Text")}
                  defaultValue={helpText}
                  onChange={(e: ChangeEvent<HTMLInputElement>) =>
                    setState(prev => ({ ...prev, helpText: e.target.value }))
                  }
                  className="input-edit-action"
                />
              </FormLayout.Group>
              <FormLayout.Group>
                <CustomSelect
                  label={t("Dropdown Options")}
                  value={state.input}
                  onChange={e => setState(prev => ({ ...prev, input: e.target.value }))}
                  suffixClick={() => addListItem()}
                  editing={editing.state}
                  inputRef={selectInputRef}
                  dropdownInline
                >
                  {/* TODO: Add drag and drop functionality to dropdown list */}
                  {state.array?.length > 0
                    ? state.array.map((value: string, index: number) => {
                      const selected =
                        (value === state.input && !editing.state) || (editing.state && value == editing.value);
                      return (
                        <React.Fragment key={value.concat(index.toString())}>
                          {selected ? (
                            <li
                              className={`selected${editing.state ? " editing" : ""}`}
                              onClick={() => selectListItem(selected, value)}
                            >
                              <span
                                style={{
                                  display: "flex",
                                  justifyContent: "space-between",
                                  alignItems: "center",
                                  fontSize: "18px",
                                }}
                              >
                                <span>
                                  {value}
                                  {value === editing.value && (
                                    <>
                                      &nbsp;
                                      <Badge type="warning">
                                        {t("Editing")}
                                      </Badge>
                                    </>
                                  )}
                                </span>
                                {editing.state ? (
                                  <ButtonNew
                                    type="text"
                                    size="small"
                                    onClick={e => listIconClick(e, value, selected)}
                                  >
                                    <FontAwesomeIcon icon={["far", "ban"]} />
                                  </ButtonNew>
                                ) : (
                                  <ButtonNew
                                    type="text"
                                    size="small"
                                    onClick={e => listIconClick(e, value, selected)}
                                  >
                                    <FontAwesomeIcon icon={["far", "edit"]} />
                                  </ButtonNew>
                                )}
                              </span>
                            </li>
                          ) : (
                            <li
                              className={`${editing.state ? "editing" : ""}`}
                              onClick={() => selectListItem(selected, value)}
                            >
                              <span
                                style={{
                                  display: "flex",
                                  justifyContent: "space-between",
                                  alignItems: "center",
                                  fontSize: "18px",
                                }}
                              >
                                <span>
                                  {value}
                                  {value === editing.value && (
                                    <>
                                      &nbsp;
                                      <Badge type="warning">
                                        {t("Editing")}
                                      </Badge>
                                    </>
                                  )}
                                </span>
                                {/* Not selected list item */}
                                {!editing.state ? (
                                  <ButtonNew
                                    type="text"
                                    size="small"
                                    onClick={e => listIconClick(e, value, selected)}
                                  >
                                    <FontAwesomeIcon icon={faXmark} />
                                  </ButtonNew>
                                ) : null}
                              </span>
                            </li>
                          )}
                        </React.Fragment>
                      );
                    })
                    : null}
                </CustomSelect>
              </FormLayout.Group>
              {!hideRequired ? (
                <FormLayout.Group>
                  <div />
                  <CheckboxBlock
                    label={t("Required")}
                    checked={state.required}
                    className="input-view-required"
                    onChange={() => setState(prev => ({ ...prev, required: !prev.required }))}
                  />
                </FormLayout.Group>
              ) : null}
            </FormLayout>
          </Form>
        </Sheet>
      </Portal>
    </div>
  );
}
